// ═══════════════════════════════════════════════════════════════
// ChapterEditor — Édition chapitre (textarea + markdown preview)
// S56 P3 — Foundation. Split horizontal simple (pas SplitChatView,
// car on veut les deux panneaux simultanés permanents).
//
// Auto-save : debounce 2s via createAutoSaver (bookApi.js).
// Pas de Save manuel. Indicateur d'état sauvegarde remonté via callback.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Code, Eye, Copy, Check } from 'lucide-react';
import MarkdownMessage from '../chat/MarkdownMessage';
import ChapterHeader from './ChapterHeader';
import ChapterChatPane from './ChapterChatPane';
import WorkChapterModal from './WorkChapterModal';
import { createAutoSaver, updateChapter, updateChapterStatus, listVersions, restoreVersion, getBook, listStyleNotes } from '../../services/bookApi';
import StyleNotesPanel from './StyleNotesPanel';
import { getTerms } from '../../utils/documentTerminology';

const CHAT_WIDTH_KEY = 'itriia_chapter_chat_width_v1';
const NOTES_OPEN_KEY = 'itriia_chapter_notes_open_v1';

const ChapterEditor = ({
    bookId,
    book,
    projectId,
    chapter,
    onChapterUpdated,
    onChapterCreated,
    onOpenVersions,
    onOpenTransition,
    nextChapterId,
}) => {
    const [content, setContent] = useState(chapter?.content_text || '');
    const [saveState, setSaveState] = useState('idle'); // idle | dirty | saving | saved | error
    const [lastSavedAt, setLastSavedAt] = useState(chapter?.updated_at || null);
    const [versionCount, setVersionCount] = useState(null);
    const [showVersionsPanel, setShowVersionsPanel] = useState(false);
    const [versions, setVersions] = useState([]);
    const [showReviseModal, setShowReviseModal] = useState(false);
    const [rightMode, setRightMode] = useState('render'); // 'render' | 'source'
    const [copiedRight, setCopiedRight] = useState(false);
    const [chatWidthPct, setChatWidthPct] = useState(() => {
        const v = parseFloat(localStorage.getItem(CHAT_WIDTH_KEY));
        return !isNaN(v) && v >= 25 && v <= 75 ? v : 55;
    });
    const [showNotesPanel, setShowNotesPanel] = useState(() => localStorage.getItem(NOTES_OPEN_KEY) === '1');
    const [notesCount, setNotesCount] = useState(0);
    const containerRef = useRef(null);
    const resizingRef = useRef(false);
    const t = getTerms(book?.genre);
    const saverRef = useRef(null);
    const chapterIdRef = useRef(chapter?.id);

    // Recharge content quand on change de chapitre
    useEffect(() => {
        chapterIdRef.current = chapter?.id;
        setContent(chapter?.content_text || '');
        setSaveState('idle');
        setLastSavedAt(chapter?.updated_at || null);
        setVersionCount(null);
        setShowVersionsPanel(false);
    }, [chapter?.id]);

    // S57-G fix : content_text arrive en async (listChapters ne le fetch pas).
    // Quand le parent livre enfin le content_text et qu'on est encore vide, on hydrate.
    // On n'écrase JAMAIS un content déjà saisi par l'user.
    useEffect(() => {
        if (!chapter?.id) return;
        if (chapter.content_text && !content) {
            setContent(chapter.content_text);
            setLastSavedAt(chapter.updated_at || null);
        }
    }, [chapter?.content_text]);

    // Init auto-saver quand on a bookId + chapterId
    useEffect(() => {
        if (!bookId || !chapter?.id) return;
        if (saverRef.current) saverRef.current.cancel();
        saverRef.current = createAutoSaver({
            bookId,
            chapterId: chapter.id,
            delayMs: 2000,
            onSaved: (result) => {
                setSaveState('saved');
                setLastSavedAt(new Date().toISOString());
                if (result?.chapter && onChapterUpdated) onChapterUpdated(result.chapter);
                if (result?.version_created) {
                    setVersionCount(v => (v == null ? v : v + 1));
                }
            },
            onError: () => {
                setSaveState('error');
            },
        });
        return () => saverRef.current?.cancel();
    }, [bookId, chapter?.id, onChapterUpdated]);

    // Flush auto-save au démontage / changement chapitre
    useEffect(() => {
        return () => {
            if (saverRef.current) saverRef.current.forceFlush().catch(() => {});
        };
    }, []);

    // Persiste la largeur du chat (debounce léger)
    useEffect(() => {
        const t = setTimeout(() => {
            try { localStorage.setItem(CHAT_WIDTH_KEY, String(chatWidthPct)); } catch {}
        }, 300);
        return () => clearTimeout(t);
    }, [chatWidthPct]);

    // S58 — Persiste l'état d'ouverture du drawer Notes
    useEffect(() => {
        try { localStorage.setItem(NOTES_OPEN_KEY, showNotesPanel ? '1' : '0'); } catch {}
    }, [showNotesPanel]);

    // S58 — Fetch compteur notes au changement de chapitre (même si drawer fermé)
    useEffect(() => {
        let cancelled = false;
        if (!bookId || !chapter?.id) { setNotesCount(0); return; }
        listStyleNotes(bookId, chapter.id)
            .then(r => { if (!cancelled) setNotesCount((r?.notes || []).length); })
            .catch(() => { if (!cancelled) setNotesCount(0); });
        return () => { cancelled = true; };
    }, [bookId, chapter?.id]);

    const handleContentChange = (e) => {
        const next = e.target.value;
        setContent(next);
        if (next !== chapter?.content_text) {
            setSaveState(s => (s === 'saving' ? s : 'dirty'));
            // Indique "saving" au prochain tick pour feedback visuel
            setTimeout(() => setSaveState(s => (s === 'dirty' ? 'saving' : s)), 2100);
        }
        saverRef.current?.push(next);
    };

    const handleRename = useCallback(async (newTitle) => {
        if (!chapter?.id) return;
        try {
            const result = await updateChapter(bookId, chapter.id, {
                metadata_updates: { title: newTitle },
            });
            if (result?.chapter && onChapterUpdated) onChapterUpdated(result.chapter);
        } catch {
            // silently ignore rename failures
        }
    }, [bookId, chapter?.id, onChapterUpdated]);

    const handleStatusChange = useCallback(async (newStatus) => {
        if (!chapter?.id) return;
        try {
            const result = await updateChapterStatus(bookId, chapter.id, newStatus);
            if (result?.chapter && onChapterUpdated) onChapterUpdated(result.chapter);
        } catch {}
    }, [bookId, chapter?.id, onChapterUpdated]);

    const handleOpenVersions = useCallback(async () => {
        if (onOpenVersions) {
            onOpenVersions();
            return;
        }
        setShowVersionsPanel(v => !v);
        if (!showVersionsPanel && chapter?.id) {
            try {
                const res = await listVersions(bookId, chapter.id);
                setVersions(res?.versions || []);
                setVersionCount((res?.versions || []).length);
            } catch {}
        }
    }, [bookId, chapter?.id, showVersionsPanel, onOpenVersions]);

    const handleRestore = useCallback(async (versionId) => {
        if (!chapter?.id) return;
        try {
            const result = await restoreVersion(bookId, chapter.id, versionId);
            if (result?.chapter) {
                setContent(result.chapter.content_text || '');
                setLastSavedAt(new Date().toISOString());
                setSaveState('saved');
                if (onChapterUpdated) onChapterUpdated(result.chapter);
                const res = await listVersions(bookId, chapter.id);
                setVersions(res?.versions || []);
                setVersionCount((res?.versions || []).length);
            }
        } catch {}
    }, [bookId, chapter?.id, onChapterUpdated]);

    if (!chapter) {
        return (
            <div style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-muted)', fontSize: 13,
            }}>
                Sélectionne {t.docArticle === 'une' ? 'une' : 'un'} {t.unitSingular.toLowerCase()} dans la navigation à gauche.
            </div>
        );
    }

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <ChapterHeader
                bookId={bookId}
                chapter={chapter}
                saveState={saveState}
                lastSavedAt={lastSavedAt}
                versionCount={versionCount ?? versions.length ?? 0}
                onRenameChapter={handleRename}
                onStatusChange={handleStatusChange}
                onOpenVersions={handleOpenVersions}
                onRevise={() => setShowReviseModal(true)}
                onConsolidate={null}  // stub S59
                onOpenTransitionToNext={(nextChapterId && onOpenTransition && chapter?.id)
                    ? () => onOpenTransition(chapter.id, nextChapterId)
                    : null}
                hasNextChapter={!!nextChapterId}
                onToggleNotes={() => setShowNotesPanel(v => !v)}
                notesCount={notesCount}
                notesOpen={showNotesPanel}
            />

            {showReviseModal && (
                <WorkChapterModal
                    bookId={bookId}
                    book={book}
                    chapter={chapter}
                    onClose={() => setShowReviseModal(false)}
                    onCompleted={(res) => {
                        if (res?.chapter) {
                            setContent(res.chapter.content_text || '');
                            setLastSavedAt(new Date().toISOString());
                            setSaveState('saved');
                            if (onChapterUpdated) onChapterUpdated(res.chapter);
                            // refresh versions list
                            listVersions(bookId, chapter.id).then(r => {
                                setVersions(r?.versions || []);
                                setVersionCount((r?.versions || []).length);
                            }).catch(() => {});
                        }
                    }}
                />
            )}

            <div ref={containerRef} style={{ flex: 1, display: 'flex', minHeight: 0, minWidth: 0, position: 'relative', overflow: 'hidden' }}>
                {/* Chat atelier — panneau gauche */}
                <div style={{ width: `${chatWidthPct}%`, minWidth: 260, display: 'flex' }}>
                    <ChapterChatPane
                        projectId={projectId}
                        bookId={bookId}
                        chapter={chapter}
                        onChapterUpdated={onChapterUpdated}
                        onChapterCreated={onChapterCreated}
                    />
                </div>

                {/* Resize handle */}
                <div
                    onMouseDown={() => {
                        resizingRef.current = true;
                        document.body.style.cursor = 'col-resize';
                        document.body.style.userSelect = 'none';
                        const onMove = (ev) => {
                            if (!resizingRef.current || !containerRef.current) return;
                            const rect = containerRef.current.getBoundingClientRect();
                            const pct = ((ev.clientX - rect.left) / rect.width) * 100;
                            const clamped = Math.max(25, Math.min(75, pct));
                            setChatWidthPct(clamped);
                        };
                        const onUp = () => {
                            resizingRef.current = false;
                            document.body.style.cursor = '';
                            document.body.style.userSelect = '';
                            window.removeEventListener('mousemove', onMove);
                            window.removeEventListener('mouseup', onUp);
                        };
                        window.addEventListener('mousemove', onMove);
                        window.addEventListener('mouseup', onUp);
                    }}
                    style={{
                        width: 4, cursor: 'col-resize',
                        background: 'var(--card-border)',
                        flexShrink: 0, transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(139,92,246,0.4)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'var(--card-border)'}
                />

                {/* Rendu / Source — panneau droit */}
                <div style={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    minWidth: 0,
                    background: rightMode === 'source' ? 'var(--card-bg)' : 'var(--card-bg-secondary)',
                }}>
                    {/* Toggle Rendu ↔ Source + bouton Copier */}
                    <div style={{
                        padding: '6px 12px',
                        borderBottom: '1px solid var(--card-border)',
                        display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end',
                    }}>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(content || '');
                                setCopiedRight(true);
                                setTimeout(() => setCopiedRight(false), 1500);
                            }}
                            disabled={!content?.trim()}
                            title={`Copier ${rightMode === 'source' ? 'la source markdown' : 'le rendu'}`}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                padding: '3px 10px', borderRadius: 4, fontSize: 11,
                                background: copiedRight ? 'rgba(16,185,129,0.15)' : 'transparent',
                                border: `1px solid ${copiedRight ? 'rgba(16,185,129,0.4)' : 'var(--card-border)'}`,
                                color: copiedRight ? '#10b981' : 'var(--text-secondary)',
                                cursor: content?.trim() ? 'pointer' : 'not-allowed',
                                opacity: content?.trim() ? 1 : 0.4,
                                marginRight: 4,
                            }}
                        >
                            {copiedRight ? <Check size={11} /> : <Copy size={11} />}
                            {copiedRight ? 'Copié' : 'Copier'}
                        </button>
                        <button onClick={() => setRightMode('render')} style={togBtn(rightMode === 'render')}>
                            <Eye size={11} /> Rendu
                        </button>
                        <button onClick={() => setRightMode('source')} style={togBtn(rightMode === 'source')}>
                            <Code size={11} /> Source
                        </button>
                    </div>

                    {rightMode === 'render' ? (
                        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
                            {content.trim().length === 0 ? (
                                <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic', textAlign: 'center', marginTop: 60 }}>
                                    Le rendu apparaîtra ici.<br />
                                    <span style={{ fontSize: 11 }}>Dialogue avec l'IA à gauche ou clique <strong>Source</strong> pour écrire directement.</span>
                                </div>
                            ) : (
                                <MarkdownMessage content={content} disableMath={false} />
                            )}
                        </div>
                    ) : (
                        <textarea
                            value={content}
                            onChange={handleContentChange}
                            placeholder={`Écris directement ${t.docArticle === 'une' ? 'cette' : 'ce'} ${t.unitSingular.toLowerCase()}… Auto-save 2s.`}
                            spellCheck="true"
                            style={{
                                flex: 1, width: '100%', resize: 'none',
                                padding: '20px 24px',
                                background: 'var(--card-bg)',
                                border: 'none',
                                color: 'var(--text)',
                                fontFamily: 'Georgia, "Times New Roman", serif',
                                fontSize: 15, lineHeight: 1.7,
                                outline: 'none',
                            }}
                        />
                    )}
                </div>

                {/* S58 — Drawer notes stylistiques (inline) */}
                {showNotesPanel && chapter?.id && (
                    <StyleNotesPanel
                        bookId={bookId}
                        chapterId={chapter.id}
                        onClose={() => setShowNotesPanel(false)}
                        onCountChanged={setNotesCount}
                    />
                )}

                {/* Drawer versions (simple overlay) */}
                {showVersionsPanel && (
                    <div style={{
                        position: 'absolute', top: 0, right: 0, height: '100%', width: 320,
                        background: 'var(--card-bg)',
                        borderLeft: '1px solid var(--card-border)',
                        zIndex: 20, overflow: 'auto',
                        boxShadow: '-10px 0 30px rgba(0,0,0,0.4)',
                    }}>
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <strong style={{ fontSize: 13, color: 'var(--text)' }}>Historique des versions</strong>
                            <button onClick={() => setShowVersionsPanel(false)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
                        </div>
                        <div style={{ padding: 8 }}>
                            {versions.length === 0 && (
                                <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: '20px 12px', textAlign: 'center' }}>
                                    Aucune version.
                                </div>
                            )}
                            {versions.map(v => (
                                <div key={v.id} style={{
                                    padding: 10, margin: '4px 0', borderRadius: 6,
                                    background: 'rgba(255,255,255,0.03)',
                                    border: '1px solid rgba(255,255,255,0.04)',
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <strong style={{ fontSize: 12, color: '#a78bfa' }}>v{v.version_number}</strong>
                                        <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{v.char_count} chars</span>
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                                        {new Date(v.created_at).toLocaleString('fr-FR')}
                                        {v.change_summary && <div style={{ marginTop: 2, fontStyle: 'italic' }}>{v.change_summary}</div>}
                                    </div>
                                    <button
                                        onClick={() => handleRestore(v.id)}
                                        style={{
                                            padding: '4px 10px', borderRadius: 4,
                                            background: 'rgba(139,92,246,0.1)',
                                            border: '1px solid rgba(139,92,246,0.3)',
                                            color: '#a78bfa', fontSize: 11, cursor: 'pointer',
                                        }}
                                    >
                                        Restaurer
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const togBtn = (active) => ({
    display: 'flex', alignItems: 'center', gap: 4,
    padding: '3px 10px', borderRadius: 4, fontSize: 11,
    background: active ? 'rgba(139,92,246,0.15)' : 'transparent',
    border: `1px solid ${active ? 'rgba(139,92,246,0.35)' : 'var(--card-border)'}`,
    color: active ? '#a78bfa' : 'var(--text-secondary)',
    cursor: 'pointer',
});

export default ChapterEditor;
